import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BankParsingReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleReportDataReady = () => {
      if (window.__REPORT_DATA__) {
        setReportData(window.__REPORT_DATA__);
        setLoading(false);
        
        // Signal that report is ready for PDF generation after a short delay
        setTimeout(() => {
          const readyDiv = document.createElement('div');
          readyDiv.id = 'report-ready';
          readyDiv.style.display = 'none';
          document.body.appendChild(readyDiv);
          console.log('[Report] Ready signal added to DOM');
        }, 1000);
      }
    };

    // Check if data is already available
    if (window.__REPORT_DATA__) {
      handleReportDataReady();
    } else {
      // Listen for the custom event from Puppeteer
      window.addEventListener('reportDataReady', handleReportDataReady);
      
      // Try to get data from URL params or localStorage as fallback
      const urlParams = new URLSearchParams(window.location.search);
      const dataId = urlParams.get('dataId');
      if (dataId) {
        // In a real implementation, fetch data by ID
        console.log('Fetching report data by ID:', dataId);
      }
      setLoading(false);
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener('reportDataReady', handleReportDataReady);
    };
  }, []);

  const computeOverallKpis = (results) => {
    const allAccounts = results.flatMap(r => r.data?.accounts || []);
    const totalAccounts = allAccounts.length;
    const totalStatements = allAccounts.length; // Each account is now a single statement
    
    let totalDeposits = 0, totalWithdrawals = 0, totalNegativeDays = 0, totalNsfs = 0;
    let totalDepositCount = 0, totalWithdrawalCount = 0;
    let statementCount = 0;

    allAccounts.forEach(account => {
      // Each account is now a single statement (no nested statements array)
      const metrics = account.metrics || {};
      totalDeposits += Math.abs(metrics.deposits || 0);
      totalWithdrawals += Math.abs(metrics.withdrawals || 0);
      totalNegativeDays += metrics.negativeDays || 0;
      totalNsfs += metrics.nsfs || 0;
      totalDepositCount += metrics.depositCount || 0;
      totalWithdrawalCount += metrics.withdrawalCount || 0;
      statementCount++;
    });

    return {
      totalAccounts,
      totalStatements,
      avgDeposits: statementCount > 0 ? (totalDeposits / statementCount) : 0,
      avgWithdrawals: statementCount > 0 ? (totalWithdrawals / statementCount) : 0,
      avgNegativeDays: statementCount > 0 ? (totalNegativeDays / statementCount) : 0,
      avgNsfs: statementCount > 0 ? (totalNsfs / statementCount) : 0,
      avgDepositCount: statementCount > 0 ? Math.round(totalDepositCount / statementCount) : 0,
      avgWithdrawalCount: statementCount > 0 ? Math.round(totalWithdrawalCount / statementCount) : 0
    };
  };

  const groupResultsByAccount = (results) => {
    const accountGroups = new Map();
    
    results.forEach(result => {
      const accounts = result.data?.accounts || [];
      accounts.forEach(account => {
        const key = account.account_last4 || 'unknown';
        if (!accountGroups.has(key)) {
          accountGroups.set(key, {
            account_last4: key,
            bank_name: account.bank_name,
            account_holder: account.account_holder,
            statements: [],
            competitorFindings: []
          });
        }
        
        const group = accountGroups.get(key);
        // Each account is now a single statement (no nested statements array)
        group.statements.push(account);
        group.competitorFindings.push(...(account.competitorFindings || []));
      });
    });
    
    return Array.from(accountGroups.values());
  };

  const buildAccountChartData = (statements) => {
    const chartData = statements.map(statement => {
      // Use pre-calculated metrics from server (single source of truth)
      const metrics = statement.metrics || { deposits: 0, withdrawals: 0 };
      const monthLabel = deriveStatementMonthLabel(statement);
      
      return {
        label: monthLabel,
        deposits: Math.abs(metrics.deposits || 0),
        withdrawals: Math.abs(metrics.withdrawals || 0)
      };
    });

    return {
      labels: chartData.map(d => d.label),
      datasets: [
        {
          label: 'Deposits',
          data: chartData.map(d => d.deposits),
          backgroundColor: 'rgba(99, 102, 241, 0.8)',
          borderColor: 'rgba(99, 102, 241, 1)',
          borderWidth: 1
        },
        {
          label: 'Withdrawals',
          data: chartData.map(d => d.withdrawals),
          backgroundColor: 'rgba(107, 114, 128, 0.8)',
          borderColor: 'rgba(107, 114, 128, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const deriveStatementMonthLabel = (statement) => {
    if (statement.statement_period?.start_date) {
      const date = new Date(statement.statement_period.start_date);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return 'Unknown Period';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return (num || 0).toFixed(1);
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">Loading report...</div>
      </Container>
    );
  }

  if (!reportData || !reportData.results) {
    return (
      <Container className="py-4">
        <div className="text-center">No report data available</div>
      </Container>
    );
  }

  const overallKpis = computeOverallKpis(reportData.results);
  const accountGroups = groupResultsByAccount(reportData.results);

  return (
    <div className="bank-parsing-report">
      <style jsx>{`
        @media print {
          .bank-parsing-report {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            color: #000;
            background: #fff;
          }
          
          .page-break {
            page-break-before: always;
          }
          
          .no-break {
            page-break-inside: avoid;
          }
          
          .chart-container {
            width: 100% !important;
            height: 300px !important;
          }
          
          .kpi-card {
            border: 1px solid #e5e7eb;
            margin-bottom: 1rem;
            padding: 1rem;
            background: #fff;
          }
          
          .competitor-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 1rem;
          }
          
          .competitor-table th,
          .competitor-table td {
            border: 1px solid #e5e7eb;
            padding: 0.5rem;
            text-align: left;
          }
          
          .competitor-table th {
            background-color: #f9fafb;
            font-weight: 600;
          }
        }
        
        .report-header {
          text-align: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e5e7eb;
        }
        
        .report-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }
        
        .report-subtitle {
          color: #6b7280;
          font-size: 1rem;
        }
      `}</style>

      <Container fluid className="px-4">
        <div className="report-header no-break">
          <h1 className="report-title">Bank Statement Analysis Report</h1>
          <p className="report-subtitle">
            Generated on {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Overall KPIs */}
        <div className="no-break mb-4">
          <h2 className="mb-3">Executive Summary</h2>
          <Row>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{overallKpis.totalAccounts}</div>
                <div className="text-muted small">Total Accounts</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{overallKpis.totalStatements}</div>
                <div className="text-muted small">Statements</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{formatCurrency(overallKpis.avgDeposits)}</div>
                <div className="text-muted small">Avg Deposits</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{overallKpis.avgDepositCount}</div>
                <div className="text-muted small">Avg Deposit Count</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{formatCurrency(overallKpis.avgWithdrawals)}</div>
                <div className="text-muted small">Avg Withdrawals</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{overallKpis.avgWithdrawalCount}</div>
                <div className="text-muted small">Avg Withdrawal Count</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{formatNumber(overallKpis.avgNegativeDays)}</div>
                <div className="text-muted small">Avg Negative Days</div>
              </div>
            </Col>
            <Col md={2}>
              <div className="kpi-card text-center">
                <div className="h3 mb-1">{formatNumber(overallKpis.avgNsfs)}</div>
                <div className="text-muted small">Avg NSFs</div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Account Details */}
        {accountGroups.map((account, accountIndex) => (
          <div key={account.account_last4} className={accountIndex > 0 ? "page-break" : ""}>
            <div className="no-break">
              <h2 className="mb-3">
                Account ending in {account.account_last4}
                {account.bank_name && <span className="text-muted"> - {account.bank_name}</span>}
              </h2>
              {account.account_holder && (
                <p className="text-muted mb-3">Account Holder: {account.account_holder}</p>
              )}
            </div>

            {/* Account KPIs */}
            <div className="no-break mb-4">
              <Row>
                <Col md={2}>
                  <div className="kpi-card text-center">
                    <div className="h4 mb-1">{account.statements.length}</div>
                    <div className="text-muted small">Statements</div>
                  </div>
                </Col>
                {(() => {
                  let totalDeposits = 0, totalWithdrawals = 0, totalNegativeDays = 0, totalNsfs = 0;
                  account.statements.forEach(statement => {
                    // Use pre-calculated metrics from server (single source of truth)
                    const metrics = statement.metrics || { deposits: 0, withdrawals: 0, negativeDays: 0, nsfs: 0 };
                    totalDeposits += Math.abs(metrics.deposits || 0);
                    totalWithdrawals += Math.abs(metrics.withdrawals || 0);
                    totalNegativeDays += metrics.negativeDays || 0;
                    totalNsfs += metrics.nsfs || 0;
                  });
                  const avgDeposits = account.statements.length > 0 ? totalDeposits / account.statements.length : 0;
                  const avgWithdrawals = account.statements.length > 0 ? totalWithdrawals / account.statements.length : 0;
                  const avgNegativeDays = account.statements.length > 0 ? totalNegativeDays / account.statements.length : 0;
                  const avgNsfs = account.statements.length > 0 ? totalNsfs / account.statements.length : 0;

                  return (
                    <>
                      <Col md={2}>
                        <div className="kpi-card text-center">
                          <div className="h4 mb-1">{formatCurrency(avgDeposits)}</div>
                          <div className="text-muted small">Avg Deposits</div>
                        </div>
                      </Col>
                      <Col md={2}>
                        <div className="kpi-card text-center">
                          <div className="h4 mb-1">{formatCurrency(avgWithdrawals)}</div>
                          <div className="text-muted small">Avg Withdrawals</div>
                        </div>
                      </Col>
                      <Col md={2}>
                        <div className="kpi-card text-center">
                          <div className="h4 mb-1">{formatNumber(avgNegativeDays)}</div>
                          <div className="text-muted small">Avg Negative Days</div>
                        </div>
                      </Col>
                      <Col md={2}>
                        <div className="kpi-card text-center">
                          <div className="h4 mb-1">{formatNumber(avgNsfs)}</div>
                          <div className="text-muted small">Avg NSFs</div>
                        </div>
                      </Col>
                    </>
                  );
                })()}
              </Row>
            </div>

            {/* Chart */}
            {account.statements.length > 0 && (
              <div className="no-break mb-4">
                <h4 className="mb-3">Monthly Activity</h4>
                <div className="chart-container" style={{ height: '300px', width: '100%' }}>
                  <Bar
                    data={buildAccountChartData(account.statements)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: true,
                          text: 'Deposits vs Withdrawals by Month'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return formatCurrency(value);
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Statement Details */}
            <div className="no-break mb-4">
              <h4 className="mb-3">Statement Details</h4>
              {account.statements.map((statement, idx) => {
                // Use pre-calculated metrics from server (single source of truth)
                const metrics = statement.metrics || { deposits: 0, withdrawals: 0, averageDailyBalance: 0, negativeDays: 0, nsfs: 0 };
                const monthLabel = deriveStatementMonthLabel(statement);
                const startDate = statement.statement_period?.start_date || 'N/A';
                const endDate = statement.statement_period?.end_date || 'N/A';

                return (
                  <div key={idx} className="mb-3 p-3" style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="mb-0">{monthLabel}</h5>
                      <small className="text-muted">{startDate} — {endDate}</small>
                    </div>
                    <Row>
                      <Col md={3}>
                        <strong>Deposits:</strong> {formatCurrency(metrics.deposits)}
                      </Col>
                      <Col md={3}>
                        <strong>Withdrawals:</strong> {formatCurrency(Math.abs(metrics.withdrawals))}
                      </Col>
                      <Col md={3}>
                        <strong>Avg Daily Balance:</strong> {formatCurrency(metrics.averageDailyBalance)}
                      </Col>
                      <Col md={3}>
                        <strong>Negative Days:</strong> {metrics.negativeDays} | <strong>NSFs:</strong> {metrics.nsfs}
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>

            {/* Competitor Findings */}
            <div className="no-break mb-4">
              <h4 className="mb-3">Potential Competitor Payments</h4>
              {account.competitorFindings && account.competitorFindings.length > 0 ? (
                <table className="competitor-table">
                  <thead>
                    <tr>
                      <th>Vendor / Description</th>
                      <th>Type</th>
                      <th>Occurrences</th>
                      <th>Timeframe</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.competitorFindings.map((finding, idx) => (
                      <tr key={idx}>
                        <td>
                          <div>
                            {finding.matchedLender ? (
                              <strong>{finding.matchedLender}</strong>
                            ) : (
                              <span>Unknown vendor</span>
                            )}
                          </div>
                          <small className="text-muted">
                            {finding.normalizedDescription}
                          </small>
                        </td>
                        <td>
                          <span className={`badge ${finding.frequency === 'daily_weekdays' ? 'bg-primary' : 'bg-secondary'}`}>
                            {finding.frequency === 'daily_weekdays' ? 'Daily (M-F)' : 'Weekly'}
                          </span>
                        </td>
                        <td>{finding.occurrences}</td>
                        <td>
                          {finding.dateRanges && finding.dateRanges.length > 0 && (
                            <span>
                              {finding.dateRanges[0].startDate} — {finding.dateRanges[finding.dateRanges.length - 1].endDate}
                            </span>
                          )}
                        </td>
                        <td>
                          {formatCurrency(finding.amount)}
                          {finding.amountRange && (
                            <div>
                              <small className="text-muted">
                                Range: {formatCurrency(finding.amountRange.min)} - {formatCurrency(finding.amountRange.max)}
                              </small>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-muted">No competitor balances detected</p>
              )}
            </div>
          </div>
        ))}
      </Container>
    </div>
  );
};

export default BankParsingReport;
